import type React from 'react';
import type { ExpandedLayout } from '../../config/constants';
import { getBookingFieldPromptText, getBookingPreparingText } from '../../config/constants';
import { useChatLanguage } from '../../context/ChatThemeContext';
import CrossIcon from '../CrossIcon';
import ExpandCenterIcon from '../icons/ExpandCenterIcon';
import PanelRightIcon from '../icons/PanelRightIcon';

interface BookingTopBarProps {
    onMinimize: () => void;
    viewLayout?: ExpandedLayout;
    onToggleLayout?: () => void;
    showLayoutToggle?: boolean;
    /** Backend-driven next required field id (e.g. 'email', 'team_size'). */
    pendingFieldId: string | null;
}

const BookingTopBar: React.FC<BookingTopBarProps> = ({
    onMinimize,
    viewLayout = 'expanded',
    onToggleLayout,
    showLayoutToggle = false,
    pendingFieldId,
}) => {
    const language = useChatLanguage();
    const text =
        getBookingFieldPromptText(pendingFieldId, language) ?? getBookingPreparingText(language);

    return (
        <div
            className="inboundx-booking-top-bar flex flex-col w-full"
            style={{ borderBottom: '1px solid var(--rw-border-subtle)' }}
        >
            <div className="flex justify-between items-center py-3 px-3 gap-1">
                {/* Left: layout toggle */}
                <div className="flex items-center gap-1">
                    {showLayoutToggle && onToggleLayout && (
                        <button
                            type="button"
                            onClick={onToggleLayout}
                            title={
                                viewLayout === 'expanded'
                                    ? 'Switch to side panel'
                                    : 'Switch to expanded view'
                            }
                            className="w-5 h-5 border-none bg-transparent cursor-pointer p-0 transition-all duration-200 ease-out hover:scale-110 hover:opacity-70"
                        >
                            {viewLayout === 'expanded' ? (
                                <PanelRightIcon
                                    width={20}
                                    height={20}
                                    color="var(--rw-icon-muted)"
                                />
                            ) : (
                                <ExpandCenterIcon
                                    width={20}
                                    height={20}
                                    color="var(--rw-icon-muted)"
                                />
                            )}
                        </button>
                    )}
                </div>

                {/* Center: field prompt — mirrors the post-conversion progress area */}
                <div className="flex items-center gap-2.5">
                    <span
                        className="whitespace-nowrap shrink-0"
                        style={{
                            fontSize: '16px',
                            lineHeight: '28px',
                            color: 'var(--rw-text-secondary)',
                            fontFamily: 'var(--rw-font-family)',
                            fontWeight: 500,
                        }}
                    >
                        {text}
                    </span>
                </div>

                {/* Right: close button */}
                <button
                    type="button"
                    onClick={onMinimize}
                    title="Minimize chat"
                    className="inboundx-expanded-chat-minimize-button w-5 h-5 border-none bg-none cursor-pointer p-0 transition-all duration-200 ease-out hover:scale-110 hover:opacity-70"
                >
                    <CrossIcon width={20} height={20} color="var(--rw-icon-muted)" />
                </button>
            </div>
        </div>
    );
};

export default BookingTopBar;
